import {
  Badge,
  Box,
  Card,
  CardBody,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { differenceInDays } from 'date-fns'
import { Calendar } from 'lucide-react'
import {
  formatCurrency,
  formatDate,
  getReservationStatusConfig,
} from '../../../../utils/property-utils'
import type { ReservationListProps } from './types'

export default function ReservationList({
  reservations,
  nextCheckIn,
  loading = false,
}: ReservationListProps) {
  if (loading) {
    return (
      <VStack spacing={6} h="100%">
        <Skeleton height="200px" borderRadius="lg" w="100%" />
        <Skeleton height="300px" borderRadius="lg" w="100%" />
        <Skeleton height="200px" borderRadius="lg" w="100%" />
      </VStack>
    )
  }

  return (
    <VStack spacing={6} h="100%">
      {/* Next Check-in */}
      {nextCheckIn && (
        <Card w="100%">
          <CardBody>
            <Heading size="sm" mb={3}>
              Próximo Check-in
            </Heading>
            <VStack spacing={3} align="stretch">
              <HStack>
                <Icon as={Calendar} color="yellow.500" />
                <Box flex="1">
                  <Text fontWeight="semibold">
                    {formatDate(nextCheckIn.check_in_date)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {differenceInDays(
                      new Date(nextCheckIn.check_in_date),
                      new Date()
                    )}{' '}
                    dias
                  </Text>
                </Box>
              </HStack>

              <Box p={3} bg="gray.50" borderRadius="md">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold" fontSize="sm">
                    {nextCheckIn.client?.name || 'Cliente'}
                  </Text>
                  <Badge
                    size="sm"
                    colorScheme={
                      getReservationStatusConfig(nextCheckIn.status).color
                    }
                    px={3}
                    py={1}
                    borderRadius="lg"
                    textTransform="none"
                  >
                    {getReservationStatusConfig(nextCheckIn.status).label}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color="gray.600">
                  {formatDate(nextCheckIn.check_in_date)} -{' '}
                  {formatDate(nextCheckIn.check_out_date)}
                </Text>
                <Text fontSize="xs" color="green.600" fontWeight="semibold">
                  {formatCurrency(nextCheckIn.reserve_total)}
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Recent Reservations */}
      <Card w="100%" flex="1">
        <CardBody>
          <Heading size="sm" mb={4}>
            Reservas Recentes
          </Heading>
          <VStack spacing={3} align="stretch">
            {reservations.length ? (
              reservations.map((reservation) => (
                <Box key={reservation.id} p={3} bg="gray.50" borderRadius="md">
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="semibold" fontSize="sm">
                      {reservation.client?.name || 'Cliente'}
                    </Text>
                    <Badge
                      size="sm"
                      colorScheme={
                        getReservationStatusConfig(reservation.status).color
                      }
                      px={3}
                      py={1}
                      borderRadius="lg"
                      textTransform="none"
                    >
                      {getReservationStatusConfig(reservation.status).label}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.600">
                    {formatDate(reservation.check_in_date)} -{' '}
                    {formatDate(reservation.check_out_date)}
                  </Text>
                  <Text fontSize="xs" color="green.600" fontWeight="semibold">
                    {formatCurrency(reservation.reserve_total)}
                  </Text>
                </Box>
              ))
            ) : (
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Nenhuma reserva encontrada
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Financial Summary */}
      <Card w="100%">
        <CardBody>
          <Heading size="sm" mb={4}>
            Resumo Financeiro
          </Heading>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm">Receita Total</Text>
              <Text fontSize="sm" fontWeight="semibold">
                {formatCurrency(
                  reservations.reduce(
                    (sum, r) => sum + Number(r.reserve_total),
                    0
                  )
                )}
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm">Total de Reservas</Text>
              <Text fontSize="sm" fontWeight="semibold">
                {reservations.length}
              </Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
